import React from 'react';
import { Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useUntypedTranslation } from '../../../hooks/useUntypedTranslation';
import { focusRing, pressable } from '../../../styles/visualLanguage';

/**
 * AdvisoryLeaveButton
 *
 * The one leave affordance every advisory surface shows: back to the
 * engagement list, keeping the engagement exactly where it stands. Extracted
 * from `AdvisoryEngagementShell`, which used to own it inline, because a
 * SECOND surface needs it now — `AdvisoryRunProgress` — and the alternative
 * was either re-deciding the styling there (two affordances that drift) or
 * wrapping the run panel in the wizard shell (which would print a step
 * counter for something that is not a counted step).
 *
 * Deliberately owns its own navigation rather than taking an `onLeave` prop.
 * There is exactly one destination and it is a static route literal, never a
 * value derived from the URL — a prop would let a caller point the same
 * affordance somewhere else, which is the drift this extraction exists to
 * prevent.
 *
 * Wording stays plain ("Leave"), never a warning about losing unsaved work,
 * because progress genuinely persists server-side: the wizard steps are
 * stage-derived from the goal document and a run is derived from
 * `analysis.status`, so re-opening the engagement from the list resumes
 * whatever it was showing.
 */
export function AdvisoryLeaveButton() {
  const navigate = useNavigate();
  const { t } = useUntypedTranslation();

  const handleLeave = () => {
    // A static route literal, never a value derived from the URL.
    navigate('/advisory');
  };

  return (
    <Button
      variant="text"
      disableRipple
      onClick={handleLeave}
      data-testid="advisory-leave"
      aria-label={t('advisory:shell.leaveAriaLabel')}
      sx={[
        focusRing,
        pressable,
        theme => ({
          fontSize: theme.visualPrimitives.leaveSize,
          color: theme.colors.inkSecondary,
          '&:hover': {
            color: theme.colors.inkPrimary,
            backgroundColor: 'transparent',
          },
        }),
      ]}
    >
      {t('advisory:shell.leave')}
    </Button>
  );
}

export default AdvisoryLeaveButton;
